import { useState, useCallback } from 'react'
import { getFunctions, httpsCallable } from 'firebase/functions'
import useHandleError from './useHandleError'
import {
  ProgressionQueryHit,
  ProgressionQueryParams,
  PaginatedProgressionQueryResult,
} from '../types/ProgressionQuery'
import { primaryServices } from '../utils/firebaseServiceContext'

const RESULTS_PER_PAGE = 100

export type PaginationState = {
  offset: number
  limit: number
  total: number
  hasMore: boolean
}

export default function useProgressionQuery() {
  const [hits, setHits] = useState<ProgressionQueryHit[]>([])
  const [loading, setLoading] = useState(false)
  const [pagination, setPagination] = useState<PaginationState>({
    offset: 0,
    limit: RESULTS_PER_PAGE,
    total: 0,
    hasMore: false,
  })
  const handleError = useHandleError()

  const executeQuery = useCallback(
    async (params: ProgressionQueryParams, offset = 0) => {
      setLoading(true)
      try {
        const progressionQuery = httpsCallable(
          primaryServices.functions,
          'progressionQuery'
        )
        const { data } = await progressionQuery({
          ...params,
          limit: RESULTS_PER_PAGE,
          offset,
        })

        const result = data as PaginatedProgressionQueryResult
        setHits(result.hits)
        setPagination({
          offset: result.offset,
          limit: RESULTS_PER_PAGE,
          total: result.total,
          hasMore: result.hasMore,
        })
      } catch (e) {
        handleError(e)
      } finally {
        setLoading(false)
      }
    },
    [handleError]
  )

  const resetResults = useCallback(() => {
    setHits([])
    setPagination({
      offset: 0,
      limit: RESULTS_PER_PAGE,
      total: 0,
      hasMore: false,
    })
  }, [])

  return {
    hits,
    loading,
    pagination,
    executeQuery,
    resetResults,
  }
}
